<!-- Performance and risk: TWR vs benchmarks, "what if", drawdown, monthly heatmap, yearly results. -->
<script lang="ts">
  import * as E from "../core/engine";
  import { app, need } from "../lib/state.svelte";
  import { fmtDate, fmtNum, heat, MONTHS, pctPlain, rangeLabel } from "../lib/format";
  import { baseOption, compactMoney, daySeries, lineSeries, palette, series, timeAxis, tipMoney, tooltipRows, valueAxis, type Opt, type TipParam } from "../lib/chart";
  import ViewHead from "../components/ViewHead.svelte";
  import Kpi from "../components/Kpi.svelte";
  import Card from "../components/Card.svelte";
  import Chart from "../components/Chart.svelte";
  import Money from "../components/Money.svelte";
  import Signed from "../components/Signed.svelte";
  import Pct from "../components/Pct.svelte";

  const m = $derived(need(app.model));
  const A = $derived(need(app.agg));
  const r = $derived(app.range);
  const cfg = $derived(need(app.data).config);
  const st = $derived(E.periodStats(m, A, r.start, r.end));
  const rf = $derived(+(cfg.riskFreeRate ?? 0.05));
  const net = $derived(!!app.tax);
  const risk = $derived(E.riskStats(m, A, st.s0, st.e, rf, net));
  const benches = $derived((cfg.benchmarks || []).filter((b) => m.priceArr[b.symbol] && m.priceSource[b.symbol] !== "none"));
  const monthly = $derived(E.monthlyReturns(m, A, net));
  const yearly = $derived(E.yearlyTable(m, A, rf));
  const twrAnnSel = $derived(net ? st.twrNetAnn : st.twrAnn);

  // monthly heatmap
  const years = $derived([...new Set(Object.keys(monthly).map((k) => k.slice(0, 4)))].sort().reverse());
  const mKey = (y: string, k: number) => `${y}-${String(k).padStart(2, "0")}`;
  const yRet = (y: string) => { let f = 1; for (let k = 1; k <= 12; k++) { const v = monthly[mKey(y, k)]; if (v !== undefined) f *= 1 + v; } return f - 1; };

  const chartData = $derived.by(() => {
    const s0 = st.s0, e = st.e;
    return { s0, e, days: m.days.slice(s0, e + 1), bs: benches.map((b) => E.benchmarkSeries(m, A, b.symbol, s0, e)) };
  });
  const twrOpt = $derived.by(() => {
    const p = palette();
    const { s0, e, days, bs } = chartData;
    const idx = E.twrIndex(A, s0, e, false);
    const idxN = net ? E.twrIndex(A, s0, e, true) : null;
    const ser: Opt[] = [lineSeries("Portfel (TWR)", days.map((d, k) => [d, idx[k] - 1]), series(0))];
    if (idxN) ser.push(lineSeries("Portfel po podatku", days.map((d, k) => [d, idxN[k] - 1]), series(1)));
    benches.forEach((b, j) => {
      const x = bs[j];
      if (x) ser.push(lineSeries(b.name as string, days.map((d, k) => [d, isNaN(x.priceIdx[k]) ? null : x.priceIdx[k] - 1]), series(j + 2)));
    });
    const pctFmt = (v: number) => fmtNum(v * 100, 0) + "%";
    return { ...baseOption(p), xAxis: timeAxis(p), yAxis: valueAxis(pctFmt, p), series: ser,
      tooltip: { ...baseOption(p).tooltip, formatter: (ps: TipParam) => tooltipRows(ps, (v) => pctPlain(v)) } };
  });
  const whatIfOpt = $derived.by(() => {
    const p = palette();
    const { s0, e, days, bs } = chartData;
    const wi: Opt[] = [lineSeries("Twój portfel", daySeries(m, A.V, s0, e), series(0)), lineSeries("Wpłacony kapitał", days.map((d, k) => [d, A.invested[s0 + k] - A.invested[s0] + A.V[s0]]), p.muted, { step: "end" })];
    benches.forEach((b, j) => { const x = bs[j]; if (x) wi.push(lineSeries(b.name as string, days.map((d, k) => [d, x.value[k]]), series(j + 2))); });
    return { ...baseOption(p), xAxis: timeAxis(p), yAxis: valueAxis(compactMoney, p, { scale: true }), series: wi,
      tooltip: { ...baseOption(p).tooltip, formatter: (ps: TipParam) => tooltipRows(ps, (v) => tipMoney(v)) } };
  });
  const ddOpt = $derived.by(() => {
    const p = palette();
    const { days } = chartData;
    return { ...baseOption(p), legend: { show: false }, xAxis: timeAxis(p), yAxis: valueAxis((v) => fmtNum(v * 100, Math.abs(risk.maxDrawdown) < 0.1 ? 1 : 0) + "%", p, { max: 0 }),
      tooltip: { ...baseOption(p).tooltip, formatter: (ps: TipParam) => tooltipRows(ps, (v) => pctPlain(v)) },
      series: [lineSeries("Obsunięcie", days.map((d, k) => [d, risk.dd[k]]), p.loss, { areaStyle: { color: p.loss, opacity: 0.12 } })] };
  });
</script>

<ViewHead title="Wyniki i ryzyko" sub="{rangeLabel(r)}{net ? ' · stopy po podatku Belki' : ''}" />
<div class="kpis">
  <Kpi label="TWR w okresie">
    {#snippet value()}<Pct v={net ? st.twrNet : st.twr} />{/snippet}
    {#snippet sub()}{#if twrAnnSel !== null}<Pct v={twrAnnSel} /> rocznie{:else}okres krótszy niż rok{/if}{/snippet}
  </Kpi>
  <Kpi label="XIRR (roczna)" sub="stopa zwrotu z Twoich wpłat">{#snippet value()}<Pct v={net ? st.xirrNet : st.xirr} />{/snippet}</Kpi>
  <Kpi label="Maks. obsunięcie" sub="{fmtDate(risk.mddPeakDay)} → {fmtDate(risk.mddTroughDay)}">{#snippet value()}<Pct v={risk.maxDrawdown} />{/snippet}</Kpi>
  <Kpi label="Zmienność (roczna)" sub="dni wzrostowe: {risk.positiveDays !== null ? fmtNum(risk.positiveDays * 100, 0) + '%' : '–'}">{#snippet value()}<Pct v={risk.vol} d={1} sign={false} />{/snippet}</Kpi>
  <Kpi label="Wskaźnik Sharpe'a" value={risk.sharpe !== null ? fmtNum(risk.sharpe, 2) : "–"} sub="Sortino {risk.sortino !== null ? fmtNum(risk.sortino, 2) : '–'} · stopa wolna od ryzyka {fmtNum(rf * 100, 1)}%" />
  <Kpi label="Najlepszy dzień">
    {#snippet value()}<Pct v={risk.bestDay} />{/snippet}
    {#snippet sub()}najgorszy: <Pct v={risk.worstDay} />{/snippet}
  </Kpi>
</div>
<div class="grid g2">
  <Card title="Stopa zwrotu (TWR) na tle benchmarków" hint="benchmarki: zmiana ceny w PLN"><Chart id="chTwr" option={twrOpt} /></Card>
  <Card title="Co gdyby: te same wpłaty w benchmark" hint="wartość Twojego portfela vs. te same przepływy zainwestowane w indeks"><Chart id="chWhatIf" option={whatIfOpt} /></Card>
</div>
<Card title="Obsunięcie od szczytu"><Chart id="chDD" class="short" option={ddOpt} /></Card>
<Card title="Miesięczne stopy zwrotu (TWR)">
  <div class="table-wrap"><table class="heat"><thead><tr><th class="l">Rok</th>{#each MONTHS as x (x)}<th>{x}</th>{/each}<th>Rok</th></tr></thead>
    <tbody>
      {#each years as y (y)}
        {@const yr = yRet(y)}
        <tr><td class="l"><b>{y}</b></td>{#each MONTHS as x, k (x)}{@const v = monthly[mKey(y, k + 1)]}<td class="cell" style={heat(v, 0.06)}>{v === undefined ? "" : pctPlain(v)}</td>{/each}
          <td class="cell" style={heat(yr, 0.25)}><b>{pctPlain(yr)}</b></td></tr>
      {/each}
    </tbody>
  </table></div>
</Card>
<Card title="Wyniki roczne" hint="podatek należny: od zysków zrealizowanych i dochodów w roku (bez rozliczenia strat z lat ubiegłych)">
  <div class="table-wrap"><table><thead><tr><th class="l">Rok</th><th>Wartość na początku</th><th>Wpłaty netto</th><th>Wartość na końcu</th><th>Wynik</th><th>TWR</th><th>XIRR</th>
    {#if net}<th>Wynik po podatku</th><th>TWR po podatku</th>{/if}<th>Zrealizowany</th><th>Dywidendy i odsetki</th><th>Koszty</th><th>Podatek należny</th></tr></thead>
    <tbody>
      {#each yearly.slice().reverse() as y (y.year)}
        <tr><td class="l"><b>{y.year}</b></td><td><Money v={y.startValue} /></td><td><Signed v={y.flows} /></td><td><Money v={y.endValue} /></td><td><Signed v={y.gross} /></td><td><Pct v={y.twr} /></td><td><Pct v={y.xirr} /></td>
          {#if net}<td><Signed v={y.net} /></td><td><Pct v={y.twrNet} /></td>{/if}<td><Signed v={y.realized} /></td><td><Money v={y.income} d={2} /></td><td><Money v={y.fees} d={2} /></td><td><Money v={y.taxDue} d={2} /></td></tr>
      {/each}
    </tbody>
  </table></div>
</Card>
